import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Sun,
  Moon,
  Utensils,
  Activity,
  Search,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check
} from "lucide-react";
import { toast } from "sonner";

const DailyJournal = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [journal, setJournal] = useState(null);
  const [whakatauki, setWhakatauki] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [aimForDay, setAimForDay] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [wairuaPracticeDone, setWairuaPracticeDone] = useState(false);
  const [whanauConnectionDone, setWhanauConnectionDone] = useState(false);
  const [endOfDayBrief, setEndOfDayBrief] = useState("");
  
  // Nutrition state
  const [nutritionEntries, setNutritionEntries] = useState([]);
  const [nutritionSummary, setNutritionSummary] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
  const [showNutritionDialog, setShowNutritionDialog] = useState(false);
  const [foodSearch, setFoodSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMealType, setSelectedMealType] = useState("breakfast");
  const [manualNutrition, setManualNutrition] = useState({
    items: "",
    servingSizes: "",
    calories: "",
    protein: "",
    carbs: "",
    fats: "",
    notes: ""
  });
  
  // Activity state
  const [activityEntries, setActivityEntries] = useState([]);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activityType: "",
    durationMinutes: "",
    intensity: "medium",
    notes: ""
  });

  useEffect(() => {
    fetchJournalData();
  }, [currentDate]);

  const fetchJournalData = async () => {
    setLoading(true);
    try {
      const [journalRes, whakataukiRes, nutritionRes, activityRes, summaryRes] = await Promise.all([
        axios.get(`${API}/journal/${currentDate}`),
        axios.get(`${API}/whakatauki`),
        axios.get(`${API}/nutrition?date=${currentDate}`),
        axios.get(`${API}/activity?date=${currentDate}`),
        axios.get(`${API}/nutrition/summary/${currentDate}`)
      ]);
      
      const j = journalRes.data;
      if (j) {
        setJournal(j);
        setAimForDay(j.aimForDay || "");
        setProgressPercent(j.progressPercent || 0);
        setWairuaPracticeDone(j.wairuaPracticeDone || false);
        setWhanauConnectionDone(j.whanauConnectionDone || false);
        setEndOfDayBrief(j.endOfDayBrief || "");
        setWhakatauki(j.whakataukiText || whakataukiRes.data.text);
      } else {
        setJournal(null);
        setAimForDay("");
        setProgressPercent(0);
        setWairuaPracticeDone(false);
        setWhanauConnectionDone(false);
        setEndOfDayBrief("");
        setWhakatauki(whakataukiRes.data.text);
      }
      
      setNutritionEntries(nutritionRes.data || []);
      setActivityEntries(activityRes.data || []);
      setNutritionSummary(summaryRes.data || { calories: 0, protein: 0, carbs: 0, fats: 0 });
    } catch (err) {
      console.error("Journal fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const saveJournal = async () => {
    setSaving(true);
    try {
      const data = {
        date: currentDate,
        whakataukiText: whakatauki,
        aimForDay,
        progressPercent,
        wairuaPracticeDone,
        whanauConnectionDone,
        endOfDayBrief
      };
      
      if (journal?.id) {
        await axios.put(`${API}/journal/${journal.id}`, data);
      } else {
        const res = await axios.post(`${API}/journal`, data);
        setJournal(res.data);
      }
      toast.success("Journal saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const searchFoods = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/foods/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error("Food search error:", err);
    }
  };

  const addFoodFromSearch = async (food) => {
    try {
      await axios.post(`${API}/nutrition`, {
        date: currentDate,
        mealType: selectedMealType,
        items: [food.name],
        servingSizes: [food.serving],
        calories: food.calories,
        protein: food.protein,
        carbs: food.carbs,
        fats: food.fats,
        aiEstimated: false
      });
      toast.success(`Added ${food.name}`);
      setShowNutritionDialog(false);
      setFoodSearch("");
      setSearchResults([]);
      fetchJournalData();
    } catch (err) {
      toast.error("Failed to add food");
    }
  };

  const addManualNutrition = async () => {
    try {
      await axios.post(`${API}/nutrition`, {
        date: currentDate,
        mealType: selectedMealType,
        items: manualNutrition.items.split(',').map(s => s.trim()),
        servingSizes: manualNutrition.servingSizes.split(',').map(s => s.trim()),
        calories: parseInt(manualNutrition.calories) || 0,
        protein: parseFloat(manualNutrition.protein) || 0,
        carbs: parseFloat(manualNutrition.carbs) || 0,
        fats: parseFloat(manualNutrition.fats) || 0,
        notes: manualNutrition.notes,
        aiEstimated: false
      });
      toast.success("Nutrition entry added");
      setShowNutritionDialog(false);
      setManualNutrition({ items: "", servingSizes: "", calories: "", protein: "", carbs: "", fats: "", notes: "" });
      fetchJournalData();
    } catch (err) {
      toast.error("Failed to add entry");
    }
  };

  const deleteNutrition = async (id) => {
    try {
      await axios.delete(`${API}/nutrition/${id}`);
      toast.success("Entry deleted");
      fetchJournalData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const addActivity = async () => {
    if (!activityForm.activityType || !activityForm.durationMinutes) {
      toast.error("Please fill in activity type and duration");
      return;
    }
    try {
      await axios.post(`${API}/activity`, {
        date: currentDate,
        activityType: activityForm.activityType,
        durationMinutes: parseInt(activityForm.durationMinutes),
        intensity: activityForm.intensity,
        notes: activityForm.notes
      });
      toast.success("Activity logged");
      setShowActivityDialog(false);
      setActivityForm({ activityType: "", durationMinutes: "", intensity: "medium", notes: "" });
      fetchJournalData();
    } catch (err) {
      toast.error("Failed to log activity");
    }
  };

  const deleteActivity = async (id) => {
    try {
      await axios.delete(`${API}/activity/${id}`);
      toast.success("Activity deleted");
      fetchJournalData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const changeDate = (days) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + days);
    setCurrentDate(date.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NZ', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="koru-spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="daily-journal">
      {/* Header with date navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => changeDate(-1)}
          className="text-[#0B3D2E]"
          data-testid="prev-day-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <h1 className="text-xl font-brand text-[#0B3D2E] tracking-wide">Daily Journal</h1>
          <p className="text-sm text-[#4A5D56]">{formatDate(currentDate)}</p>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => changeDate(1)}
          disabled={currentDate >= new Date().toISOString().split('T')[0]}
          className="text-[#0B3D2E]"
          data-testid="next-day-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Whakataukī Card */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-5" data-testid="whakatauki-card">
        <CardContent className="p-6">
          <p className="font-whakatauki text-xl text-[#051F17] text-center leading-relaxed">
            &ldquo;{whakatauki}&rdquo;
          </p>
        </CardContent>
      </Card>

      {/* Start of Day */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-5" data-testid="start-of-day-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sun className="w-4 h-4 text-[#D4A84B]" />
            <h3 className="font-brand text-[#051F17]">Start of Day</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-[#4A5D56]">Aim for the Day</Label>
              <Textarea
                placeholder="What do you want to achieve today?"
                value={aimForDay}
                onChange={(e) => setAimForDay(e.target.value)}
                className="mt-2 min-h-[80px] bg-[#F9F9F7] border-0 rounded-xl focus:ring-2 focus:ring-[#0B3D2E]/20"
                data-testid="aim-input"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-[#4A5D56]">Daily Progress</Label>
                <span className="text-sm font-semibold text-[#0B3D2E]">{progressPercent}%</span>
              </div>
              <Slider
                value={[progressPercent]}
                onValueChange={(v) => setProgressPercent(v[0])}
                max={100}
                step={5}
                className="w-full"
                data-testid="progress-slider"
            />
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Nutrition Section */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-5" data-testid="nutrition-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Utensils className="w-4 h-4 text-[#D4A84B]" />
              <h3 className="font-brand text-[#051F17]">Nutrition</h3>
            </div>
            <Dialog open={showNutritionDialog} onOpenChange={setShowNutritionDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary h-8 text-xs" data-testid="add-food-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Food
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Log Nutrition</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="search">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="search">Search Foods</TabsTrigger>
                    <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="search" className="space-y-4">
                    <div>
                      <Label>Meal Type</Label>
                      <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                        <SelectTrigger className="tribe-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakfast">Breakfast</SelectItem>
                          <SelectItem value="lunch">Lunch</SelectItem>
                          <SelectItem value="dinner">Dinner</SelectItem>
                          <SelectItem value="snack">Snack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA39B]" />
                      <Input
                        placeholder="Search NZ foods..."
                        value={foodSearch}
                        onChange={(e) => {
                          setFoodSearch(e.target.value);
                          searchFoods(e.target.value);
                        }}
                        className="pl-9 tribe-input"
                        data-testid="food-search-input"
                      />
                    </div>
                    <ScrollArea className="h-[200px]">
                      {searchResults.map((food, i) => (
                        <div 
                          key={i}
                          className="flex items-center justify-between p-3 hover:bg-[#E3EBE5]/50 rounded-lg cursor-pointer"
                          onClick={() => addFoodFromSearch(food)}
                        >
                          <div>
                            <p className="font-medium text-sm">{food.name}</p>
                            <p className="text-xs text-[#4A5D56]">{food.serving}</p>
                          </div>
                          <div className="text-right text-xs text-[#4A5D56]">
                            <p>{food.calories} cal</p>
                            <p>P:{food.protein}g C:{food.carbs}g F:{food.fats}g</p>
                          </div>
                        </div>
                      ))}
                      {foodSearch && searchResults.length === 0 && (
                        <p className="text-center text-sm text-[#4A5D56] py-4">
                          No foods found. Try manual entry.
                        </p>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="manual" className="space-y-3">
                    <div>
                      <Label>Meal Type</Label>
                      <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                        <SelectTrigger className="tribe-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakfast">Breakfast</SelectItem>
                          <SelectItem value="lunch">Lunch</SelectItem>
                          <SelectItem value="dinner">Dinner</SelectItem>
                          <SelectItem value="snack">Snack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Food Items (comma separated)</Label>
                      <Input
                        placeholder="e.g., Rice, Chicken, Vegetables"
                        value={manualNutrition.items}
                        onChange={(e) => setManualNutrition({...manualNutrition, items: e.target.value})}
                        className="tribe-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Calories</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={manualNutrition.calories}
                          onChange={(e) => setManualNutrition({...manualNutrition, calories: e.target.value})}
                          className="tribe-input"
                        />
                      </div>
                      <div>
                        <Label>Protein (g)</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={manualNutrition.protein}
                          onChange={(e) => setManualNutrition({...manualNutrition, protein: e.target.value})}
                          className="tribe-input"
                        />
                      </div>
                      <div>
                        <Label>Carbs (g)</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={manualNutrition.carbs}
                          onChange={(e) => setManualNutrition({...manualNutrition, carbs: e.target.value})}
                          className="tribe-input"
                        />
                      </div>
                      <div>
                        <Label>Fats (g)</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={manualNutrition.fats}
                          onChange={(e) => setManualNutrition({...manualNutrition, fats: e.target.value})}
                          className="tribe-input"
                        />
                      </div>
                    </div>
                    <Button onClick={addManualNutrition} className="w-full btn-primary">
                      Add Entry
                    </Button>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Daily Summary */}
          <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-[#E3EBE5]/30 rounded-xl">
            <div className="text-center">
              <p className="text-lg font-bold text-[#0B3D2E]">{nutritionSummary.calories}</p>
              <p className="text-[10px] text-[#4A5D56]">Calories</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#B85C4C]">{nutritionSummary.protein}g</p>
              <p className="text-[10px] text-[#4A5D56]">Protein</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#D4A84B]">{nutritionSummary.carbs}g</p>
              <p className="text-[10px] text-[#4A5D56]">Carbs</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#5B8BA0]">{nutritionSummary.fats}g</p>
              <p className="text-[10px] text-[#4A5D56]">Fats</p>
            </div>
          </div>

          {/* Entries */}
          <div className="space-y-2">
            {nutritionEntries.length === 0 ? (
              <p className="text-center text-sm text-[#8FA39B] py-4">
                No meals logged yet
              </p>
            ) : (
              nutritionEntries.map((entry) => (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-[#F9F9F7] rounded-xl"
                >
                  <div>
                    <p className="text-xs font-medium text-[#0B3D2E] uppercase">{entry.mealType}</p>
                    <p className="text-sm text-[#051F17]">{entry.items.join(', ')}</p>
                    <p className="text-xs text-[#4A5D56]">
                      {entry.calories} cal | P:{entry.protein}g C:{entry.carbs}g F:{entry.fats}g
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteNutrition(entry.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activity Section */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-5" data-testid="activity-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#B85C4C]" />
              <h3 className="font-brand text-[#051F17]">Activity</h3>
            </div>
            <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary h-8 text-xs" data-testid="add-activity-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Activity
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Log Activity</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Activity Type</Label>
                    <Input
                      placeholder="e.g., Running, Gym, Swimming"
                      value={activityForm.activityType}
                      onChange={(e) => setActivityForm({...activityForm, activityType: e.target.value})}
                      className="tribe-input"
                      data-testid="activity-type-input"
                    />
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      placeholder="30"
                      value={activityForm.durationMinutes}
                      onChange={(e) => setActivityForm({...activityForm, durationMinutes: e.target.value})}
                      className="tribe-input"
                      data-testid="activity-duration-input"
                    />
                  </div>
                  <div>
                    <Label>Intensity</Label>
                    <Select 
                      value={activityForm.intensity} 
                      onValueChange={(v) => setActivityForm({...activityForm, intensity: v})}
                    >
                      <SelectTrigger className="tribe-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      placeholder="How did it feel?"
                      value={activityForm.notes}
                      onChange={(e) => setActivityForm({...activityForm, notes: e.target.value})}
                      className="tribe-input"
                    />
                  </div>
                  <Button onClick={addActivity} className="w-full btn-primary" data-testid="save-activity-btn">
                    Save Activity
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="space-y-2">
            {activityEntries.length === 0 ? (
              <p className="text-center text-sm text-[#8FA39B] py-4">
                No activities logged yet
              </p>
            ) : (
              activityEntries.map((entry) => (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-[#F9F9F7] rounded-xl"
                >
                  <div>
                    <p className="font-medium text-[#051F17]">{entry.activityType}</p>
                    <p className="text-xs text-[#4A5D56]">
                      {entry.durationMinutes} mins • {entry.intensity} intensity
                    </p>
                    {entry.notes && (
                      <p className="text-xs text-[#8FA39B] mt-1">{entry.notes}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteActivity(entry.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* End of Day */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-5" data-testid="end-of-day-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Moon className="w-4 h-4 text-[#5B8BA0]" />
            <h3 className="font-brand text-[#051F17]">End of Day</h3>
          </div>
          <Label className="text-sm text-[#4A5D56]">Reflect on your day</Label>
          <Textarea
            placeholder="What went well? What challenged you? What did you learn?"
            value={endOfDayBrief}
            onChange={(e) => setEndOfDayBrief(e.target.value)}
            className="mt-2 min-h-[100px] bg-[#F9F9F7] border-0 rounded-xl focus:ring-2 focus:ring-[#0B3D2E]/20"
            data-testid="end-of-day-input"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button 
        onClick={saveJournal}
        disabled={saving}
        className="w-full h-14 bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full font-semibold"
        data-testid="save-journal-btn"
      >
        {saving ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        ) : (
          <>
            <Check className="w-4 h-4 mr-2" /> Save Journal
          </>
        )}
      </Button>
    </div>
  );
};

export default DailyJournal;
